import React, { useEffect, useRef, useState } from 'react';
import toast, { ToastOptions } from 'react-hot-toast';
import dayjs, { Dayjs } from 'dayjs';
import { useBlocklistApi } from 'src/modules/blocklist/api/BlocklistApi';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import { convertHtmlToText } from 'src/helpers/helpers';
import { FileLinkWithMimeType } from 'src/constants/types';
import { ChatFooterProps } from '../types';
import { getApiErrorMessage } from 'src/api/axiosClient/errorParser';
import { logSafeAxiosError, reportErrorToUser } from 'src/api/axiosClient/errorHandling';
import { GENERIC_ERROR_MESSAGE } from 'src/constants/messages';

export const useChatFooterController = (props: ChatFooterProps) => {
  const {
    messageTemplates,
    addMessage,
    updateLinks,
    selectedContactObject,
    isCompanyInSandBoxMode,
    isCompanyHasSubscription,
    newMessage,
    scheduledMessageToEdit,
    clearScheduledMessageToEdit,
    updateScheduledMessage,
    defaultCustomFields,
    handleFocus,
    isMobile,
  } = props;

  const { openDialog, ConfirmationDialog } = useConfirmationDialog();
  const [message, setMessage] = useState<string>('');
  const [showAttachFileModal, setAttachFileModal] = useState<boolean>(false);
  const [url, setUrl] = useState<string>('');
  const [mimeType, setMimeType] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAttachmentProcessing, setIsAttachmentProcessing] = useState<boolean>(false);
  const [isMessageValid, setIsMessageValid] = useState<boolean>(true);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState<boolean>(false);
  const [dateScheduled, setDateScheduled] = useState<Dayjs | null>(null);
  const [pendingDateScheduled, setPendingDateScheduled] = useState<string | undefined>(undefined);

  const validateText = useBlocklistApi().validate;
  const [showbannedWordsModal, setShowBannedWordsModal] = useState<boolean>(false);
  const [isContactInvalid] = useState<boolean>(
    selectedContactObject.contact.validStatus == 'invalid',
  );
  const [isContactOptedOut] = useState<boolean>(!!selectedContactObject.contact.optOutDate);
  const sendButtonRef = useRef<HTMLButtonElement>(null);
  const [toolbarContainer, setToolbarContainer] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    setMessage('');
    setUrl('');
    setMimeType(undefined);
    setIsAttachmentProcessing(false);
    setDateScheduled(null);
    setPendingDateScheduled(undefined);
    clearScheduledMessageToEdit();
    updateLinks();
  }, [selectedContactObject.contact.id]);

  useEffect(() => {
    if (!scheduledMessageToEdit) {
      return;
    }

    setMessage(scheduledMessageToEdit.text);

    if (scheduledMessageToEdit.dateScheduled) {
      const scheduledDate = dayjs(scheduledMessageToEdit.dateScheduled);
      if (scheduledDate.isValid()) {
        setDateScheduled(scheduledDate);
      }
    }

    setIsScheduleDialogOpen(true);
  }, [scheduledMessageToEdit]);

  const handleTextUpdate = (newText: string) => {
    setMessage(newText);
  };

  const isMessageEmpty = () => {
    const textContent = convertHtmlToText(message).trim();
    return textContent.length === 0;
  };

  const isActionDisabled =
    isContactInvalid ||
    isContactOptedOut ||
    isAttachmentProcessing ||
    isMessageEmpty() ||
    !isMessageValid;

  const showScheduleTimeInvalidToast = () => {
    toast.error('Scheduled time must be in the future.', { position: 'bottom-right' });
  };

  const isScheduleDateValid = (scheduleDate?: string): boolean => {
    if (!scheduleDate) {
      return true;
    }

    const parsed = dayjs(scheduleDate);
    return parsed.isValid() && parsed.isAfter(dayjs());
  };

  const handleMessageSave = async (newText?: string, scheduleDate?: string): Promise<boolean> => {
    const messageToSend = newText ?? message;
    const textContent = convertHtmlToText(messageToSend).trim();

    if (textContent.length === 0) {
      return false;
    }

    if (isContactInvalid || isContactOptedOut) {
      const options: ToastOptions = {
        position: 'bottom-right',
      };
      if (isContactInvalid) {
        toast.error(
          'This user has an invalid phone number and is not able to receive text messages.',
          options,
        );
      } else if (isContactOptedOut) {
        toast.error(
          'This user has opted out from receiving messages. They must opt back in before you are able to message them.',
          options,
        );
      }
      return false;
    }

    const resolvedScheduleDate =
      scheduleDate ??
      (scheduledMessageToEdit?.dateScheduled
        ? dayjs(scheduledMessageToEdit.dateScheduled).toISOString()
        : undefined);

    if (scheduledMessageToEdit && !resolvedScheduleDate) {
      toast.error('Select a valid scheduled time before saving this message.', {
        position: 'bottom-right',
      });
      return false;
    }

    if (!isScheduleDateValid(resolvedScheduleDate)) {
      showScheduleTimeInvalidToast();
      return false;
    }

    const onSendSuccess = () => {
      setMessage('');
      setUrl('');
      setMimeType('');
      updateLinks();
      setDateScheduled(null);
      setPendingDateScheduled(undefined);
      clearScheduledMessageToEdit();
    };

    setIsLoading(true);

    try {
      const savePromise = scheduledMessageToEdit && resolvedScheduleDate
        ? updateScheduledMessage(scheduledMessageToEdit, resolvedScheduleDate, textContent)
        : addMessage(textContent, resolvedScheduleDate);
      await savePromise;
      onSendSuccess();
      if (isMobile) {
        setTimeout(() => {
          const editor = document.querySelector('.ProseMirror');
          if (editor) {
            (editor as HTMLElement).focus();
          }
        }, 100);
      }
      return true;
    } catch (e) {
      // 406/409/422/424 are left to the caller, so show the server's reason.
      reportErrorToUser(e, getApiErrorMessage(e, GENERIC_ERROR_MESSAGE));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const validateBlockListAndSave = async (
    skipValidation: boolean = false,
    newText?: string,
    scheduleDate?: string,
  ) => {
    if (!isCompanyHasSubscription && !isCompanyInSandBoxMode) {
      openDialog({
        title: 'Subscription Required',
        content: 'An active subscription is required to send messages.',
        onConfirm: () => {},
        confirmButtonText: 'OK',
        confirmButtonColor: 'primary',
        cancelButtonVariant: 'hidden'
      });
      return;
    }

    if (skipValidation) {
      const didSave = await handleMessageSave(newText, scheduleDate);
      if (didSave) {
        setMessage('');
      }
    } else {
      validateText({ texts: [message] })
        .then(async () => {
          const didSave = await handleMessageSave(newText, scheduleDate);
          if (didSave) {
            setMessage('');
            setPendingDateScheduled(undefined);
          }
        })
        .catch((error: { code: number }) => {
          if (error.code === 422) {
            setPendingDateScheduled(scheduleDate);
            setShowBannedWordsModal(true);
          }
          logSafeAxiosError(error);
        });
    }
  };

  const openScheduleDialog = () => {
    if (!scheduledMessageToEdit) {
      setDateScheduled(null);
    }
    setIsScheduleDialogOpen(true);
  };

  const handleScheduleDialogClose = () => {
    setIsScheduleDialogOpen(false);

    if (!scheduledMessageToEdit) {
      return;
    }

    setMessage('');
    setDateScheduled(null);
    setPendingDateScheduled(undefined);
    clearScheduledMessageToEdit();
  };

  const handleScheduleMessage = () => {
    if (!dateScheduled) {
      return;
    }

    const nextDateScheduled = dateScheduled.toISOString();
    if (!isScheduleDateValid(nextDateScheduled)) {
      showScheduleTimeInvalidToast();
      return;
    }

    validateBlockListAndSave(false, undefined, nextDateScheduled);
    setIsScheduleDialogOpen(false);
  };

  const handleRemoveAttachment = () => {
    setUrl('');
    setMimeType(undefined);
    updateLinks();
  };

  const handleAttachFile = (link: FileLinkWithMimeType) => {
    setUrl(link.url);
    setMimeType(link.mimeType);
    setIsAttachmentProcessing(true);
    updateLinks({ url: link.url, mimeType: link.mimeType });
    setIsAttachmentProcessing(false);
  };

  const handleEditorBlur = () => {
    setTimeout(() => {
      const activeElement = document.activeElement;
      const tagName = activeElement?.tagName;
      const isStillFocusedOnEditor =
        !!activeElement?.closest('.ProseMirror') ||
        !!activeElement?.closest('.tiptap') ||
        (isMobile && (tagName === 'INPUT' || tagName === 'TEXTAREA'));

      if (!isStillFocusedOnEditor) {
        handleFocus?.(false);
      }
    }, isMobile ? 300 : 100);
  };

  const handleBannedWordsConfirm = () => {
    validateBlockListAndSave(true, undefined, pendingDateScheduled);
    setShowBannedWordsModal(false);
    setPendingDateScheduled(undefined);
  };

  const handleBannedWordsCancel = () => {
    setShowBannedWordsModal(false);
    setPendingDateScheduled(undefined);
  };

  const handleSendClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isMobile) {
      e.preventDefault();
      e.stopPropagation();
    }
    validateBlockListAndSave();
  };

  return {
    ConfirmationDialog,
    message,
    messageTemplates,
    defaultCustomFields,
    newMessage,
    url,
    mimeType,
    isLoading,
    isMessageValid,
    isScheduleDialogOpen,
    dateScheduled,
    showbannedWordsModal,
    isContactInvalid,
    isContactOptedOut,
    isActionDisabled,
    isCompanyHasSubscription,
    isCompanyInSandBoxMode,
    showAttachFileModal,
    setAttachFileModal,
    sendButtonRef,
    toolbarContainer,
    setToolbarContainer,
    setIsMessageValid,
    setDateScheduled,
    handleTextUpdate,
    validateBlockListAndSave,
    openScheduleDialog,
    handleScheduleDialogClose,
    handleScheduleMessage,
    handleRemoveAttachment,
    handleAttachFile,
    handleEditorBlur,
    handleBannedWordsConfirm,
    handleBannedWordsCancel,
    handleSendClick,
    handleFocus,
    isMobile,
  };
};
